import React, { useCallback, useEffect, useState } from 'react';
import {
  Breadcrumbs,
  Flex,
  P,
  Pagination,
  SearchInput,
} from '@decisiv/ui-components';
import { useNotifications } from '@decisiv/ui-components/lib/components/Notifications';
import { t, Trans } from '@lingui/macro';
import noop from 'lodash/noop';
import debounce from 'lodash/debounce';
import PageTitle from '../../components/PageTitle/PageTitle';
import IntegrationsApi from '../../api/integrations';
import SharedSettingsApi from '../../api/shared_settings';
import { IntegrationsWrapper } from '../../containers/IntegrationContainer/IntegrationsWrapper.styled';
import IntegrationContainer from '../../containers/IntegrationContainer';
import WrapperWithLoading from '../../components/WrapperWithLoading';
import Helmet from '../../components/SEO/Helmet';
import restoreErrorMessage from '../../utils/restoreErrorMessage';
import RestoreModal from './RestoreModal';

const PAGE_SIZE = 12;

export default function ArchivePage() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allSharedSettings, setAllSharedSettings] = useState([]);
  const [integrationToRestore, setIntegrationToRestore] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  const { notify } = useNotifications();

  const totalPages = Math.ceil(totalRecords / PAGE_SIZE);

  const notificationSuccessProps = {
    intent: 'success',
    title: t`Integration Restored`,
    duration: 3500,
    onClose: noop,
  };
  const notificationErrorProps = {
    intent: 'danger',
    title: t`Something went wrong!`,
    duration: 3500,
    onClose: noop,
  };

  const getArchivedIntegrations = useCallback(async () => {
    setLoading(true);
    const response = await IntegrationsApi.fetchArchivedIntegrations(
      currentPage,
      PAGE_SIZE,
      { name: filterSearch || null },
    );
    setIntegrations(response.data || []);
    setTotalRecords(response.totalRecords || 0);
    setLoading(false);
  }, [currentPage, filterSearch]);

  useEffect(() => {
    getArchivedIntegrations();
  }, [getArchivedIntegrations]);

  useEffect(() => {
    async function fetchSettings() {
      const { data } = await SharedSettingsApi.fetchAllSharedSettings();
      if (data) {
        setAllSharedSettings(
          data.map((item) => ({ id: item.id, ...item.attributes })),
        );
      }
    }

    fetchSettings();
  }, []);

  const applySearch = useCallback(
    debounce((value) => {
      setFilterSearch(value);
      setCurrentPage(1);
    }, 500),
    [],
  );

  const onSearchInputChange = (event) => {
    setSearchInput(event.target.value);
    applySearch(event.target.value);
  };

  const closeRestoreModal = () => setIntegrationToRestore(null);

  const handleRestore = async () => {
    if (!integrationToRestore) return;

    setRestoring(true);
    const { id, name } = integrationToRestore;
    const response = await IntegrationsApi.restoreIntegration(id);

    if (response.status === 200) {
      notify(notificationSuccessProps, t`${name} has been restored`);
      closeRestoreModal();
      // Step back a page when the last card on a non-first page is restored,
      // otherwise just refresh the current page.
      if (integrations.length === 1 && currentPage > 1) {
        setCurrentPage((page) => page - 1);
      } else {
        await getArchivedIntegrations();
      }
    } else {
      const detail =
        restoreErrorMessage(response.errors) ||
        t`Attempt to restore ${name} failed.`;
      notify(notificationErrorProps, detail);
      closeRestoreModal();
    }

    setRestoring(false);
  };

  const rangeStart = totalRecords === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, totalRecords);

  return (
    <>
      <Helmet
        title={t`Archived Integrations`}
        description={t`Archived Integrations`}
      />
      <Flex padding={1}>
        <Breadcrumbs
          path={[{ to: '/integrations', text: 'Integrations' }]}
          current="Archive"
        />
      </Flex>
      <PageTitle title="Archived Integrations">
        <SearchInput
          value={searchInput}
          width={32}
          label={t`Search archived integrations`}
          name="archivedNameSearch"
          placeholder={t`Search by name`}
          onChange={onSearchInputChange}
          size="small"
          searchDisabled={false}
        />
      </PageTitle>
      <WrapperWithLoading loading={loading}>
        <IntegrationsWrapper title="integrations-container">
          {integrations.map(({ id, attributes }) => (
            <IntegrationContainer
              key={id}
              integration={attributes}
              integrationId={id}
              locationUrl="/integrations/archive"
              archiveMode
              allSharedSettings={allSharedSettings}
              onRestore={() =>
                setIntegrationToRestore({ id, name: attributes.name })
              }
            />
          ))}
        </IntegrationsWrapper>
        {totalRecords > 0 && (
          <Flex
            padding={2}
            alignItems="center"
            justifyContent="space-between"
            name="archive-pagination"
          >
            <P shade={1}>
              <Trans>
                {rangeStart} - {rangeEnd} of {totalRecords} archived
                integrations
              </Trans>
            </P>
            {totalPages > 1 && (
              <Pagination
                totalPages={totalPages}
                activePage={currentPage}
                onPageChange={setCurrentPage}
              />
            )}
          </Flex>
        )}
      </WrapperWithLoading>
      <RestoreModal
        visible={Boolean(integrationToRestore)}
        loading={restoring}
        integrationName={integrationToRestore?.name}
        onRestore={handleRestore}
        onClose={closeRestoreModal}
      />
    </>
  );
}
